import {
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  IsArray,
  MaxLength,
  Min,
  Max,
  IsEnum,
} from 'class-validator'
import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger'
import { Transform } from 'class-transformer'
import {
  PedagogicContentState,
  ContentVisibility,
} from '../../../enums/content.enum'
import {
  MAX_LESSON_TITLE_LENGTH,
  MAX_LESSON_DESCRIPTION_LENGTH,
  MIN_LESSON_DURATION,
  MAX_LESSON_DURATION,
} from '../../../constants/lesson.constants'

const toNumber = ({ value }: { value: any }) => {
  if (value === '' || value === null || value === undefined) return undefined
  const num = Number(value)
  return isNaN(num) ? value : num
}

export class CreateLessonDto {
  @ApiProperty({
    description: 'Titre de la leçon',
    example: 'Leçon 1 - Introduction',
    maxLength: MAX_LESSON_TITLE_LENGTH,
    type: 'string',
  })
  @IsNotEmpty({ message: 'Le titre de la leçon est requis' })
  @IsString()
  @MaxLength(MAX_LESSON_TITLE_LENGTH, {
    message: `Le titre ne peut pas dépasser ${MAX_LESSON_TITLE_LENGTH} caractères`,
  })
  title!: string

  @ApiPropertyOptional({
    description: 'Description détaillée de la leçon',
    maxLength: MAX_LESSON_DESCRIPTION_LENGTH,
    type: 'string',
  })
  @IsOptional()
  @IsString()
  @MaxLength(MAX_LESSON_DESCRIPTION_LENGTH, {
    message: `La description ne peut pas dépasser ${MAX_LESSON_DESCRIPTION_LENGTH} caractères`,
  })
  description?: string

  @ApiProperty({
    description: 'ID du type de leçon',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsNotEmpty({ message: 'Le type de leçon est requis' })
  @IsUUID('4', { message: "L'ID du type doit être un UUID valide" })
  typeId!: string

  @ApiProperty({
    description: 'Durée de la leçon en minutes',
    example: 60,
    minimum: MIN_LESSON_DURATION,
    maximum: MAX_LESSON_DURATION,
    type: Number,
  })
  @IsNotEmpty({ message: 'La durée de la leçon est requise' })
  @Transform(toNumber)
  @IsNumber({}, { message: 'La durée doit être un nombre' })
  @Min(MIN_LESSON_DURATION, {
    message: `La durée doit être au moins ${MIN_LESSON_DURATION} minutes`,
  })
  @Max(MAX_LESSON_DURATION, {
    message: `La durée ne peut pas dépasser ${MAX_LESSON_DURATION} minutes`,
  })
  duration!: number

  @ApiProperty({
    description: 'ID du chapitre parent',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsNotEmpty({ message: 'Le chapitre est requis' })
  @IsUUID('4', { message: "L'ID du chapitre doit être un UUID valide" })
  chapterId!: string

  @ApiProperty({
    description: 'ID du statut de la leçon',
    example: '550e8400-e29b-41d4-a716-446655440001',
    type: 'string',
  })
  @IsNotEmpty({ message: 'Le statut de la leçon est requis' })
  @IsUUID('4', { message: "L'ID du statut doit être un UUID valide" })
  statusId!: string

  @ApiProperty({
    description: 'ID de la matière (dénormalisé)',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsNotEmpty({ message: 'La matière est requise' })
  @IsUUID('4')
  subjectExternalId!: string

  @ApiProperty({
    description: 'ID du niveau pédagogique (dénormalisé)',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsNotEmpty({ message: 'Le niveau est requis' })
  @IsUUID('4')
  levelExternalId!: string

  @ApiPropertyOptional({
    description: "ID de l'établissement",
    example: '660e8400-e29b-41d4-a716-446655440001',
    type: 'string',
  })
  @IsOptional()
  @IsUUID('4')
  establishmentId?: string

  @ApiPropertyOptional({
    description: 'Ordre dans le chapitre',
    example: 1,
    type: Number,
  })
  @IsOptional()
  @Transform(toNumber)
  @IsNumber()
  @Min(1)
  orderInChapter?: number

  @ApiPropertyOptional({
    description: 'État de la leçon',
    enum: PedagogicContentState,
    default: PedagogicContentState.ACTIVE,
  })
  @IsOptional()
  @IsEnum(PedagogicContentState)
  state?: PedagogicContentState

  @ApiPropertyOptional({
    description: 'Visibilité de la leçon',
    enum: ContentVisibility,
    default: ContentVisibility.PRIVATE,
  })
  @IsOptional()
  @IsEnum(ContentVisibility)
  visibility?: ContentVisibility

  @ApiPropertyOptional({
    description: 'IDs des concepts du programme à associer à la leçon',
    type: [String],
    example: ['550e8400-e29b-41d4-a716-446655440000'],
  })
  @IsOptional()
  @IsArray()
  @IsUUID('4', { each: true })
  programConceptIds?: string[]
}
